'use client'
import {track as trackMossAnalytics} from '@getmoss/moss-analytics'
import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import type {SliceComponentProps} from '@prismicio/react'
import Image from 'next/image'
import {useRouter, useSearchParams} from 'next/navigation'
import {linkResolver} from 'prismicio'
import {useEffect} from 'react'
import {Container} from 'src/common/Container'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'

/**
 * Props for `Redirect`.
 */
export type RedirectProps = SliceComponentProps<Content.RedirectSlice>

/**
 * Component for "Redirect" Slices.
 */
const Redirect = ({slice}: RedirectProps): JSX.Element => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const domainCountry = useGetDomainCountry()

  // Redirect based on query param match or fallback to default redirect URL
  useEffect(() => {
    const redirects = [...slice.primary.conditional_redirects]
    const params = searchParams.toString()

    if (redirects.length && params) {
      const match = redirects.find(
        (entry) => entry.param_value && params.toLowerCase().includes(entry.param_value.toLowerCase()),
      )

      if (match && isFilled.link(match.redirect_url)) {
        const targetUrl = linkResolver(match.redirect_url)

        trackMossAnalytics({
          event: 'page_load',
          page: {
            domainCountry,
            type: 'redirect',
            details: {param: match.param_value, targetUrl},
          },
        })

        router.push(targetUrl)
        return
      }
    }

    // Fallback to default redirect
    if (isFilled.link(slice.primary?.redirect_url)) {
      router.push(linkResolver(slice.primary.redirect_url))
    }
  }, [router, searchParams, slice.primary, domainCountry])

  return (
    <Container
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      backgroundColor="Dark Green"
      className="h-full min-h-[100vh] flex-col items-center !py-0"
    >
      <div className="flex flex-1 items-center justify-center">
        <Image src="/icons/logo.svg" alt="Moss" width={150} height={36} className="invert motion-safe:animate-pulse" />
      </div>
    </Container>
  )
}

export default Redirect
